import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import slide1 from '../images/two.jpeg';
import slide2 from '../images/one.jpeg';
import slide3 from '../images/three.jpeg';
import ContactModal from './ContactModal';

const Hero = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);

  const slides = [slide1, slide2, slide3];

  /* Calendly */
  useEffect(() => {
    if (window.Calendly) {
      setCalendlyLoaded(true);
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => setCalendlyLoaded(true);
    document.body.appendChild(script);

    return () => {
      link.remove();
      script.remove();
    };
  }, []);

  const openCalendly = () => {
    window.Calendly?.initPopupWidget({
      url: 'https://calendly.com/dev-lakche/30min',
    });
  };

  /* Auto-slide */
  useEffect(() => {
    const timer = setInterval(
      () => setCurrentSlide((prev) => (prev + 1) % slides.length),
      6000
    );
    return () => clearInterval(timer);
  }, [slides.length]);

  const stats = [
    { topLabel: t('stats.years_exp'), number: '10+', bottomLabel: t('stats.solutions') },
    { topLabel: t('stats.reliability'), number: '99%', bottomLabel: t('stats.satisfaction') },
    { topLabel: t('stats.scalability'), number: '3x', bottomLabel: t('stats.growth') },
    { topLabel: t('stats.trust'), number: '∞', bottomLabel: t('stats.possibilities') },
  ];

  return (
    <div className="relative overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center">
        {/* Background slideshow */}
        <div className="absolute inset-0 z-0">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide}
                alt=""
                loading="eager"
                sizes="100vw"
                className="h-full w-full object-cover scale-105"
              />
              <div className="absolute inset-0 bg-black/60 backdrop-blur-[1px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12">
          <div className="mx-auto max-w-7xl grid lg:grid-cols-2 gap-16 items-center pt-32 pb-40">
            <div>
              <h1 className="text-white font-black leading-tight tracking-tight">
                <span className="block text-4xl sm:text-5xl lg:text-6xl">
                  {t('hero.title1')}
                </span>
                <span className="block bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent text-5xl sm:text-6xl lg:text-7xl mt-2">
                  {t('hero.title2')}
                </span>
                <span className="block text-3xl sm:text-4xl lg:text-5xl mt-3">
                  {t('hero.title3')}
                </span>
              </h1>

              <p className="mt-8 max-w-xl text-gray-200 text-base sm:text-lg leading-relaxed">
                {t('hero.description')}
              </p>

              {/* CTA */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openCalendly}
                  disabled={!calendlyLoaded}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-white font-semibold shadow-xl hover:shadow-2xl transition focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50"
                >
                  {t('hero.cta_products')}
                  <span aria-hidden>→</span>
                </button>

                <button
                  onClick={() => setIsContactModalOpen(true)}
                  className="inline-flex items-center justify-center rounded-xl border-2 border-white/80 px-8 py-4 text-white font-semibold hover:bg-white hover:text-blue-700 transition"
                >
                  {t('hero.cta_quote')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Slide dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`h-2 rounded-full transition-all ${
                i === currentSlide ? 'w-10 bg-white' : 'w-3 bg-white/50'
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="-mt-28 relative z-30 px-4 sm:px-6 lg:px-12">
        <div className="mx-auto max-w-7xl rounded-2xl overflow-hidden bg-white shadow-2xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="p-10 sm:p-14 text-center border-b sm:border-b-0 sm:border-r last:border-none"
            >
              <p className="text-gray-500 font-medium mb-4">{stat.topLabel}</p>
              <p className="text-5xl font-light text-gray-900 mb-3">
                {stat.number}
              </p>
              <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                {stat.bottomLabel}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div className="h-24 bg-white" />

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
  );
};

export default Hero;
